import { z } from "zod";
import { RoleSchema } from "./common.js";
import { SupportedCurrencyCodesSchema } from "./currencies.js";
/** Public-subset user (returned by `GET /users/{userId}` to any authenticated caller). */
export const PublicUserSchema = z.strictObject({
    id: z.string().uuid(),
    displayName: z.string().min(1).max(64),
});
/** Self-view (returned by `GET /users/me`); includes private fields like email. */
export const UserSchema = z.strictObject({
    id: z.string().uuid(),
    displayName: z.string().min(1).max(64),
    email: z.string().email(),
    /**
     * The user's chosen display currency, used to convert cross-currency
     * balances into a single figure on summary screens. Optional because rows
     * created before the preference existed (and brand-new sign-ups) have none;
     * clients fall back to a timezone-derived default via
     * `resolvePreferredCurrency`.
     */
    preferredCurrency: SupportedCurrencyCodesSchema.optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/**
 * A group the caller and the viewed user are BOTH in, as returned by
 * `GET /users/{userId}/profile`. Only groups the caller already belongs to are
 * ever listed, so the profile view can't be used to enumerate someone's
 * group memberships.
 */
export const SharedGroupSchema = z.strictObject({
    id: z.string().uuid(),
    label: z.string().min(1).max(120),
    currency: SupportedCurrencyCodesSchema,
    /** The viewed user's role in this group — already visible on its roster. */
    role: RoleSchema,
});
/**
 * Response of `GET /users/{userId}/profile`: who someone is, and where you
 * overlap with them.
 *
 * Deliberately a *narrow* view. It carries the public identity fields plus the
 * groups you share, and nothing else — no email, no credit balance or ledger,
 * no preferred currency, no membership the caller can't already see. Anything
 * account-private stays on `/users/me` and the admin surface (issue #452).
 */
export const UserProfileViewSchema = z.strictObject({
    user: PublicUserSchema,
    sharedGroups: z.array(SharedGroupSchema),
});
/** Body of `PATCH /users/me`. */
export const UpdateMeRequestSchema = z.strictObject({
    displayName: z.string().min(1).max(64).optional(),
});
/** Body of `PATCH /users/me/settings`. */
export const UpdateSettingsRequestSchema = z.strictObject({
    preferredCurrency: SupportedCurrencyCodesSchema.optional(),
});
